import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Github, Linkedin, Download, Command } from "lucide-react";
import { navLinks, personalInfo } from "../data/mock";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled
            ? "bg-[#050507]/80 backdrop-blur-xl border-b border-white/5"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <a href="#hero" className="flex items-center gap-2.5 group">
            <div className="relative w-9 h-9 rounded-lg glass flex items-center justify-center group-hover:glow-cyan transition-shadow">
              <span className="font-display text-sm font-bold text-cyan-300">VB</span>
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 ring-2 ring-[#050507]" />
            </div>
            <div className="hidden sm:block">
              <p className="font-display text-base font-semibold text-white leading-none">
                {personalInfo.name}
              </p>
              <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-zinc-500 mt-1">
                v1.0.0 · online
              </p>
            </div>
          </a>

          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="px-3 py-2 rounded-lg text-sm text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => {
                const ev = new KeyboardEvent("keydown", { key: "k", metaKey: true });
                window.dispatchEvent(ev);
              }}
              className="inline-flex items-center gap-2 px-3 h-9 rounded-lg glass hover:border-cyan-400/30 text-zinc-400 hover:text-white text-xs font-mono transition-colors"
              aria-label="Open command palette"
            >
              <Command className="w-3 h-3" />
              <span>Search</span>
              <kbd className="ml-2 px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[10px] text-zinc-400">
                ⌘ K
              </kbd>
            </button>
            <a
              href={personalInfo.github}
              target="_blank"
              rel="noreferrer"
              className="w-9 h-9 rounded-lg glass flex items-center justify-center text-zinc-400 hover:text-cyan-300 hover:border-cyan-400/30 transition-colors"
              aria-label="GitHub"
            >
              <Github className="w-4 h-4" />
            </a>
            <a
              href={personalInfo.linkedin}
              target="_blank"
              rel="noreferrer"
              className="w-9 h-9 rounded-lg glass flex items-center justify-center text-zinc-400 hover:text-cyan-300 hover:border-cyan-400/30 transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin className="w-4 h-4" />
            </a>
            <a
              href={personalInfo.resumeUrl}
              target="_blank"
              rel="noreferrer"
              className="ml-1 inline-flex items-center gap-2 px-4 h-9 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-zinc-950 text-sm font-semibold transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Resume
            </a>
          </div>

          <button
            onClick={() => setOpen(true)}
            className="md:hidden w-10 h-10 rounded-lg glass flex items-center justify-center text-zinc-200"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] md:hidden bg-[#050507]/95 backdrop-blur-2xl"
          >
            <div className="flex items-center justify-between px-5 h-16 border-b border-white/5">
              <span className="font-display text-base font-semibold text-white">
                {personalInfo.name}
              </span>
              <button
                onClick={() => setOpen(false)}
                className="w-10 h-10 rounded-lg glass flex items-center justify-center text-zinc-200"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex flex-col p-6 gap-2">
              {navLinks.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => setOpen(false)}
                  className="px-4 py-4 rounded-xl glass text-lg font-display text-white"
                >
                  {l.label}
                </motion.a>
              ))}
              <a
                href={personalInfo.resumeUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center justify-center gap-2 px-4 h-12 rounded-xl bg-cyan-400 text-zinc-950 font-semibold"
              >
                <Download className="w-4 h-4" />
                Download Resume
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
